/* Shared, server-authoritative Hit Hero cart state. */
(function () {
  'use strict';

  if (window.HHCartTruth) return;

  var listeners = [];
  var requestSeq = 0;
  var appliedSeq = 0;
  var resyncRun = 0;
  var firstReadRun = 0;
  var started = false;
  var guardProductId = '';
  var state = { status: 'loading', byVariant: {}, qty: 0, raw: null };

  function rootPath() {
    return window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  function copyByVariant(source) {
    var copy = {};
    var key;
    for (key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) copy[key] = source[key];
    }
    return copy;
  }

  function publicState() {
    return {
      status: state.status,
      byVariant: copyByVariant(state.byVariant),
      qty: state.qty,
      raw: state.raw
    };
  }

  /* One listener must not be able to take down the others, or the caller. setState runs
     synchronously inside refresh() and afterAdd(), so a throw from any subscriber's render
     used to travel straight back out of those and into whoever called them, and from inside
     a .catch handler there is nothing left to catch it. Each listener is isolated here so a
     broken section fails alone and cart truth itself stays correct. */
  function emit() {
    var snapshot = publicState();
    var index;
    for (index = 0; index < listeners.length; index += 1) {
      try {
        listeners[index](snapshot);
      } catch (error) {
        /* Reported, never rethrown. Swallowing it silently would hide a broken section.
           The logger carries its own guard because this store's traffic runs inside in-app
           webviews that inject their own JS, so console.error cannot be assumed sound, and a
           throw from the reporter would defeat the isolation it exists to report on. */
        try {
          if (window.console && typeof window.console.error === 'function') {
            window.console.error('HHCartTruth listener failed', error);
          }
        } catch (ignored) {}
      }
    }
  }

  function setState(next) {
    state = {
      status: next.status,
      byVariant: next.byVariant || {},
      qty: Number(next.qty) || 0,
      raw: next.raw || null
    };
    emit();
  }

  function configure() {
    var node = document.querySelector('[data-hh-cart-truth]');
    var configured = node ? String(node.getAttribute('data-guard-product-id') || '') : '';
    guardProductId = configured.split('/').pop();
    return guardProductId;
  }

  function eligibleLines(cart) {
    var byVariant = {};
    var qty = 0;
    var items = cart && cart.items;
    var index;
    var item;
    var variantId;
    var amount;
    if (!Array.isArray(items)) throw new Error('Invalid cart response');
    for (index = 0; index < items.length; index += 1) {
      item = items[index];
      if (String(item.product_id) !== guardProductId) continue;
      variantId = String(item.variant_id || item.id || '');
      amount = Number(item.quantity) || 0;
      if (!variantId || amount <= 0) continue;
      byVariant[variantId] = (byVariant[variantId] || 0) + amount;
      qty += amount;
    }
    return { byVariant: byVariant, qty: qty };
  }

  /* Responses are ordered by when the read began, rather than arrival time. */
  function applyCartTruth(cart, seq, status) {
    var lines;
    if (typeof seq === 'number') {
      if (seq < appliedSeq) return false;
      appliedSeq = seq;
    }
    lines = eligibleLines(cart);
    setState({ status: status || 'resolved', byVariant: lines.byVariant, qty: lines.qty, raw: cart });
    return true;
  }

  function fetchCartTruth(status) {
    var seq;
    configure();
    if (!guardProductId) return Promise.reject(new Error('Hit Hero guard product is not configured'));
    requestSeq += 1;
    seq = requestSeq;
    return fetch(rootPath() + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('Cart request failed');
        return response.json();
      })
      .then(function (cart) {
        applyCartTruth(cart, seq, status);
        return cart;
      });
  }

  function failClosed() {
    setState({ status: 'unresolved', byVariant: state.byVariant, qty: state.qty, raw: state.raw });
  }

  function refresh() {
    /* Any caller of refresh() takes ownership of the status from here on, which cancels the
       startup retry ladder in start(). See firstRead below. */
    firstReadRun += 1;
    setState({ status: state.raw ? 'syncing' : 'loading', byVariant: state.byVariant, qty: state.qty, raw: state.raw });
    return fetchCartTruth().catch(function (error) {
      failClosed();
      throw error;
    });
  }

  /* A cart fingerprint, so a resync resolves when the cart has ACTUALLY changed rather
     than when a timer expires. Horizon dispatches shopify:cart:lines-update before its
     own /cart/change request completes, so a fixed 450ms read can capture the old cart
     and publish it as resolved. After a drawer REMOVAL that leaves the shopper priced at
     a better tier than checkout will charge, which is the one thing this build must never
     do. */
  function cartFingerprint(cart) {
    var parts = [];
    var i;
    if (!cart) return '';
    parts.push(String(cart.item_count || 0));
    if (cart.items && cart.items.length) {
      for (i = 0; i < cart.items.length; i += 1) {
        parts.push(String(cart.items[i].variant_id) + ':' + String(cart.items[i].quantity));
      }
    }
    return parts.join('|');
  }

  var RESYNC_DELAYS = [0, 400, 900, 1600];

  function scheduleCartResync(event) {
    /* Our own add paths already did the authoritative work before dispatching: they POST, then
       await afterAdd(), which reads /cart.js and resolves state. By the time this event fires the
       baseline below is ALREADY the post-add cart, so the ladder can never observe a fingerprint
       change and always runs to exhaustion, holding status:'syncing' (disabled button, "Updating
       cart") for ~3.5s after the add was verified. Measured 2026-08-03 in WebKit: click to
       re-enable was 5,162ms on the buy card with 5 /cart.js reads, where 1 is sufficient.
       The ladder is still required for Horizon-origin drawer edits, where the event fires BEFORE
       the mutation lands and the baseline is genuinely stale, so only our own sources skip it. */
    if (event && event.detail && (event.detail.source === 'hh-buy-card' || event.detail.source === 'hh-pack-builder')) {
      return;
    }
    var run = resyncRun + 1;
    resyncRun = run;
    var baseline = cartFingerprint(state.raw);
    setState({ status: 'syncing', byVariant: state.byVariant, qty: state.qty, raw: state.raw });

    function settle() {
      if (run !== resyncRun) return;
      setState({ status: 'resolved', byVariant: state.byVariant, qty: state.qty, raw: state.raw });
    }

    function attempt(index) {
      if (run !== resyncRun) return;
      if (index >= RESYNC_DELAYS.length) {
        /* Nothing we can see changed. The edit may have been to another product, or a
           no-op. Resolving on the freshest read we have is correct here. */
        settle();
        return;
      }
      window.setTimeout(function () {
        if (run !== resyncRun) return;
        fetchCartTruth('syncing')
          .then(function () {
            if (run !== resyncRun) return;
            if (cartFingerprint(state.raw) !== baseline) settle();
            else attempt(index + 1);
          })
          .catch(function () {
            if (run !== resyncRun) return;
            if (index + 1 >= RESYNC_DELAYS.length) failClosed();
            else attempt(index + 1);
          });
      }, RESYNC_DELAYS[index]);
    }

    /* Horizon attaches a promise to the event on some paths. If it is there, wait for the
       mutation to land before polling at all. */
    var pending = event && (event.promise || (event.detail && event.detail.promise));
    if (pending && typeof pending.then === 'function') {
      pending.then(function () { attempt(0); }, function () { attempt(0); });
    } else {
      attempt(0);
    }
  }

  function afterAdd(requestedItems) {
    var before = copyByVariant(state.byVariant);
    var items = Array.isArray(requestedItems) ? requestedItems : [];
    return refresh().then(function () {
      var confirmed = [];
      var index;
      var requested;
      var variantId;
      var wanted;
      var beforeQty;
      var afterQty;
      var increased;
      for (index = 0; index < items.length; index += 1) {
        requested = items[index] || {};
        variantId = String(requested.id || requested.variant_id || '');
        wanted = Number(requested.quantity) || 0;
        beforeQty = Number(before[variantId]) || 0;
        afterQty = Number(state.byVariant[variantId]) || 0;
        increased = Math.max(0, afterQty - beforeQty);
        confirmed.push({
          id: variantId,
          requested: wanted,
          before: beforeQty,
          after: afterQty,
          confirmed: Math.min(wanted, increased),
          increased: increased >= wanted && wanted > 0
        });
      }
      return { state: publicState(), variants: confirmed };
    });
  }

  function subscribe(fn) {
    if (typeof fn !== 'function') return function () {};
    listeners.push(fn);
    fn(publicState());
    return function () {
      var index = listeners.indexOf(fn);
      if (index > -1) listeners.splice(index, 1);
    };
  }

  window.HHCartTruth = {
    subscribe: subscribe,
    get: publicState,
    refresh: refresh,
    afterAdd: afterAdd
  };

  /* First read only. A single dropped /cart.js used to be terminal, which on an in-app
     webview over cellular is an ordinary event rather than an exceptional one, and it left the
     shopper on a product page with no price and a dead Add to Cart. Retry a couple of times
     before calling it, then fail closed exactly as before.

     The chain is superseded, not merely delayed. refresh() bumps firstReadRun, so a
     visibilitychange, pageshow or afterAdd read that lands mid-ladder ends the ladder. Without
     that, a stale retry failing AFTER a newer read had resolved called failClosed() over the
     good result and disabled Add to Cart for the rest of the page view. */
  var FIRST_READ_DELAYS = [500, 1500];
  function firstRead(attempt, run) {
    if (run !== firstReadRun || state.status === 'resolved') return;
    setState({ status: state.raw ? 'syncing' : 'loading', byVariant: state.byVariant, qty: state.qty, raw: state.raw });
    fetchCartTruth().catch(function () {
      if (run !== firstReadRun || state.status === 'resolved') return;
      if (attempt >= FIRST_READ_DELAYS.length) {
        failClosed();
        return;
      }
      window.setTimeout(function () { firstRead(attempt + 1, run); }, FIRST_READ_DELAYS[attempt]);
    });
  }

  function start() {
    if (started) return;
    started = true;
    firstReadRun += 1;
    firstRead(0, firstReadRun);
    document.addEventListener('shopify:cart:lines-update', scheduleCartResync);
    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) refresh().catch(function () {});
    });
    window.addEventListener('pageshow', function (event) {
      if (event && event.persisted) refresh().catch(function () {});
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}());
